import { memo } from 'react';
import { NodeProps, Handle, Position } from 'reactflow';

export const ConditionNode = memo((props: NodeProps) => {
  const getDescription = () => {
    const mode = props.data.config?.mode || 'simple';

    if (mode === 'switch') {
      const cases = props.data.config?.cases || [];
      return `${cases.length} condição(ões)`;
    }

    const type = props.data.config?.conditionType;
    if (!type) return 'Configure a condição';

    const labels: Record<string, string> = {
      contains: 'Contém texto',
      equals: 'Igual a',
      regex: 'Expressão regular',
      variable: 'Variável',
    };

    return labels[type] || type;
  };

  const isConfigured = props.data.config && Object.keys(props.data.config).length > 0;
  const mode = props.data.config?.mode || 'simple';
  const cases = props.data.config?.cases || [];

  // No modo switch, criar um handle para cada case + default
  const handleCount = mode === 'switch' ? cases.length + 1 : 2;

  return (
    <div
      onClick={props.data.onClick}
      className="relative bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer min-w-[200px] group"
      style={{
        borderLeftWidth: 4,
        borderLeftColor: '#eab308',
      }}
    >
      {/* Botão de deletar */}
      {props.data.onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            props.data.onDelete();
          }}
          className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600 z-10 shadow-md"
          title="Deletar node"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-gray-300 dark:!bg-gray-500 !border-2 !border-white dark:!border-gray-700 !shadow-sm"
      />

      {/* Conteúdo */}
      <div className="px-4 py-2.5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm">❓</span>
          <span className="font-semibold text-sm text-gray-900 dark:text-white">
            {mode === 'switch' ? 'Switch' : 'Condição'}
          </span>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{getDescription()}</p>

        {isConfigured && (
          <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-xs font-medium text-emerald-700 dark:text-emerald-300">
            <i className="fas fa-check text-emerald-600 dark:text-emerald-400" aria-hidden />
            Configurado
          </div>
        )}

        {!isConfigured && (
          <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs font-medium text-amber-700 dark:text-amber-300">
            <i className="fas fa-info-circle text-amber-600 dark:text-amber-400" aria-hidden />
            Clique para configurar
          </div>
        )}

        {/* Labels das saídas para modo switch */}
        {mode === 'switch' && cases.length > 0 && (
          <div className="mt-2 space-y-1">
            {cases.map((caseItem: any, index: number) => (
              <div key={index} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: `hsl(${(index * 360) / handleCount}, 70%, 60%)` }} />
                <span className="truncate">{caseItem.label || caseItem.value}</span>
              </div>
            ))}
            <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-400 shrink-0" />
              <span>Padrão</span>
            </div>
          </div>
        )}
      </div>

      {/* Handles de saída */}
      {mode === 'switch' ? (
        <>
          {cases.map((caseItem: any, index: number) => (
            <Handle
              key={`case-${index}`}
              type="source"
              position={Position.Right}
              id={`case-${index}`}
              className="w-3 h-3 !border-2 !border-white dark:!border-gray-700 !shadow-sm"
              style={{
                backgroundColor: `hsl(${(index * 360) / handleCount}, 70%, 60%)`,
                top: `${((index + 1) * 100) / (handleCount + 1)}%`,
              }}
              title={caseItem.label || caseItem.value}
            />
          ))}
          <Handle
            type="source"
            position={Position.Right}
            id="default"
            className="w-3 h-3 !bg-gray-300 dark:!bg-gray-500 !border-2 !border-white dark:!border-gray-700 !shadow-sm"
            style={{
              top: `${((cases.length + 1) * 100) / (handleCount + 1)}%`,
            }}
            title="Padrão (nenhuma condição atendida)"
          />
        </>
      ) : (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            className="w-3 h-3 !bg-emerald-500 !border-2 !border-white dark:!border-gray-700 !shadow-sm"
            style={{ top: '40%' }}
            title="Verdadeiro"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="false"
            className="w-3 h-3 !bg-red-500 !border-2 !border-white dark:!border-gray-700 !shadow-sm"
            style={{ top: '60%' }}
            title="Falso"
          />
        </>
      )}
    </div>
  );
});

ConditionNode.displayName = 'ConditionNode';
